export type MapBounds = {
  north: number
  south: number
  east: number
  west: number
}

const EARTH_RADIUS_KM = 6371
const DEFAULT_BOUNDS_EPSILON = 0.0001

export function areBoundsClose(
  a: MapBounds,
  b: MapBounds,
  epsilon: number = DEFAULT_BOUNDS_EPSILON,
): boolean {
  return (
    Math.abs(a.north - b.north) < epsilon &&
    Math.abs(a.south - b.south) < epsilon &&
    Math.abs(a.east - b.east) < epsilon &&
    Math.abs(a.west - b.west) < epsilon
  )
}

export function getBoundsCenter(bounds: MapBounds): { lat: number; lng: number } {
  const lat = (bounds.north + bounds.south) / 2
  if (bounds.west <= bounds.east) {
    return { lat, lng: (bounds.east + bounds.west) / 2 }
  }
  // Dateline-crossing: shift east by 360, average, then normalize back
  let lng = (bounds.west + (bounds.east + 360)) / 2
  if (lng > 180) lng -= 360
  return { lat, lng }
}

function getLongitudeSpanDegrees(bounds: MapBounds): number {
  if (bounds.west <= bounds.east) {
    return bounds.east - bounds.west
  }
  return (180 - bounds.west) + (bounds.east + 180)
}

function getLatitudeSpanDegrees(bounds: MapBounds): number {
  return Math.abs(bounds.north - bounds.south)
}

function getLongitudeSpanKm(latitude: number, spanDegrees: number): number {
  const latitudeRadians = (latitude * Math.PI) / 180
  const circumferenceAtLat =
    2 * Math.PI * EARTH_RADIUS_KM * Math.cos(latitudeRadians)
  return circumferenceAtLat * (spanDegrees / 360)
}

function getLatitudeSpanKm(spanDegrees: number): number {
  const circumference = 2 * Math.PI * EARTH_RADIUS_KM
  return circumference * (spanDegrees / 360)
}

export function getMaxVisibleKm(bounds: MapBounds): number {
  const midLat = (bounds.north + bounds.south) / 2
  const lonSpan = Math.min(Math.max(getLongitudeSpanDegrees(bounds), 0), 360)
  const latSpan = Math.min(Math.max(getLatitudeSpanDegrees(bounds), 0), 180)
  const widthKm = getLongitudeSpanKm(midLat, lonSpan)
  const heightKm = getLatitudeSpanKm(latSpan)
  return Math.max(widthKm, heightKm)
}

export function isPointInBounds(
  point: { lat: number; lng: number },
  bounds: MapBounds,
): boolean {
  const latOk = point.lat >= bounds.south && point.lat <= bounds.north
  const crossesDateline = bounds.west > bounds.east
  const lngOk = crossesDateline
    ? point.lng >= bounds.west || point.lng <= bounds.east
    : point.lng >= bounds.west && point.lng <= bounds.east
  return latOk && lngOk
}

/**
 * Converts MapBounds to the URL string format expected by svc-trip:
 * "south,west,north,east" (lat_lo,lng_lo,lat_hi,lng_hi)
 */
export function mapBoundsToUrlValue(bounds: MapBounds): string {
  return `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`
}
